import type {TypedUseSelectorHook} from 'react-redux'
import {
  // THIS SHOULD BE THE SINGLE INSTANCE WHERE WE ACTUALLY IMPORT THESE!
  // eslint-disable-next-line no-restricted-imports
  useDispatch as baseUseDispatch,
  // eslint-disable-next-line no-restricted-imports
  useSelector as baseUseSelector,
} from 'react-redux'
import type {AppDispatch, RootState} from '../store'

// The default hooks `useDispatch` and `useSelector` are not typed to AppDispatch and RootState
// Instead of importing these types every time, the functions below are pre-typed equivalents of
// the `useDispatch` and `useSelector` hooks.
// search terms, since `useSelector` often escapes me: useRedux, useStore

/**
 * A hook to access the redux `dispatch` function.
 *
 * @returns redux store's `dispatch` function
 *
 * @example
 *
 * import React, { useCallback } from 'react'
 * import { useDispatch } from '~/redux/hooks'
 *
 * export const CounterComponent = ({ value }) => {
 *   const dispatch = useDispatch()
 *   return (
 *     <div>
 *       <span>{value}</span>
 *       <button onClick={() => dispatch({ type: 'increase-counter' })}>
 *         Increase counter
 *       </button>
 *     </div>
 *   )
 * }
 */
export const useDispatch = () => baseUseDispatch<AppDispatch>()

/**
 * A hook to access the redux store's state. This hook takes a selector function
 * as an argument. The selector is called with the store state.
 *
 * This hook takes an optional equality comparison function as the second parameter
 * that allows you to customize the way the selected state is compared to determine
 * whether the component needs to be re-rendered.
 *
 * @param selector the selector function
 * @param equalityFn the function that will be used to determine equality
 *
 * @returns the selected state
 *
 * @example
 *
 * import React from 'react'
 * import { useSelector } from '~/redux/hooks'
 *
 * export const CounterComponent = () => {
 *   const counter = useSelector((state) => state.counter)
 *   return <div>{counter}</div>
 * }
 */
export const useSelector: TypedUseSelectorHook<RootState> = baseUseSelector
export const useRedux = useSelector
